/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {SiteGroupApiService, SiteGroupModel} from './site-group-api.service';
import {SiteGroupItemPageComponent} from './site-group-item-page.component';
import {SiteGroupListPageComponent} from './site-group-list-page.component';

export function siteGroupStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(SiteGroupApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasNetwork('MANAGE_SYSTEM');
  const rh = adnRouterHelper.createRouterHelper('site-groups.site-group', SiteGroupApiService, 'app.inv', 'app');

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: SiteGroupListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      siteGroupPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<SiteGroupModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api, 'Site Group'
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Site Group'));
      },
      model: function(siteGroupPage: SignalAdminResourcePageState<SiteGroupModel>) {
        return siteGroupPage.model();
      }
    },
    views: {
      'content@app': {component: SiteGroupItemPageComponent, bindings: {state: 'siteGroupPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'siteGroupPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'siteGroupPage'}}
    }
  }));
  return states.build();
}
